<template>
    <div class="pending-queue">
        <b-overlay :show="loading" rounded opacity="0.55" spinner-small spinner-variant="primary">
            <b-table
                striped
                hover
                responsive
                :items="tableData"
                :fields="fields"
                show-empty
                class="queue-table mb-0"
                thead-class="queue-thead"
            >
                <!-- Property -->
                <template #cell(property)="row">
                    <div class="cell-property">
                        <router-link
                            v-if="row.item.property_id"
                            :to="{ name: 'property-edit', params: { id: row.item.property_id } }"
                            class="property-code"
                        >
                            {{ row.item.property_code || '-' }}
                        </router-link>
                        <span v-else class="property-code">{{ row.item.property_code || '-' }}</span>
                        <span v-if="row.item.place_name" class="text-subtle">{{ row.item.place_name }}</span>
                    </div>
                </template>

                <!-- Owner -->
                <template #cell(user_name)="row">
                    <div class="cell-owner">
                        <feather-icon icon="UserIcon" size="14" class="text-muted mr-50" />
                        <router-link
                            v-if="row.item.user_id"
                            :to="{ name: 'clients-edit', params: { id: row.item.user_id } }"
                            class="owner-link"
                        >
                            {{ row.item.user_name || '-' }}
                        </router-link>
                        <span v-else>{{ row.item.user_name || '-' }}</span>
                    </div>
                </template>

                <!-- Prices -->
                <template #cell(sale_price)="row">
                    <span v-if="row.item.sale_price" class="price">฿{{ formatPrice(row.item.sale_price) }}</span>
                    <span v-else class="text-muted">–</span>
                </template>

                <template #cell(rent_price)="row">
                    <span v-if="row.item.rent_price" class="price">฿{{ formatPrice(row.item.rent_price) }}</span>
                    <span v-else class="text-muted">–</span>
                </template>

                <!-- Submitted -->
                <template #cell(updated_at)="row">
                    <span
                        v-if="row.item.updated_at"
                        v-b-tooltip.hover
                        :title="formatDate(row.item.updated_at)"
                        class="text-subtle"
                    >
                        {{ timeAgo(row.item.updated_at) }}
                    </span>
                    <span v-else class="text-muted">–</span>
                </template>

                <!-- Actions -->
                <template #cell(actions)="row">
                    <div class="cell-actions">
                        <b-button
                            title="Approve update"
                            size="sm"
                            variant="flat-success"
                            class="btn-icon"
                            :disabled="actingId === row.item.id"
                            @click="approveTemp(row.item)"
                        >
                            <b-spinner v-if="actingId === row.item.id" small />
                            <feather-icon v-else icon="CheckIcon" size="16" />
                        </b-button>

                        <b-button
                            :title="row.detailsShowing ? 'Hide details' : 'View details'"
                            size="sm"
                            variant="flat-secondary"
                            class="btn-icon"
                            @click="row.toggleDetails"
                        >
                            <feather-icon
                                :icon="row.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                                size="16"
                            />
                        </b-button>

                        <b-button
                            title="Reject update"
                            size="sm"
                            variant="flat-danger"
                            class="btn-icon"
                            :disabled="actingId === row.item.id"
                            @click="rejectTemp(row.item)"
                        >
                            <feather-icon icon="Trash2Icon" size="16" />
                        </b-button>
                    </div>
                </template>

                <!-- Expanded detail -->
                <template #row-details="row">
                    <div class="detail-panel">
                        <div class="detail-panel__title">
                            Submitted values — {{ row.item.property_code }}
                        </div>
                        <dl class="detail-grid">
                            <div v-for="d in detailItems(row.item)" :key="d.label" class="detail-grid__item">
                                <dt>{{ d.label }}</dt>
                                <dd>{{ d.value }}</dd>
                            </div>
                        </dl>
                    </div>
                </template>

                <!-- Empty -->
                <template #empty>
                    <div class="queue-empty">
                        <feather-icon icon="CheckCircleIcon" size="32" class="text-success mb-1" />
                        <p class="mb-0">Nothing waiting for review.</p>
                        <small class="text-muted">Owner-submitted updates will appear here.</small>
                    </div>
                </template>
            </b-table>
        </b-overlay>

        <!-- Footer / pagination -->
        <div v-if="pagination.totalRecords > pagination.pageSize" class="queue-footer">
            <span class="text-subtle">
                Showing {{ fromRecord }}–{{ toRecord }} of {{ pagination.totalRecords }}
            </span>

            <b-pagination
                :value="pagination.currentPage"
                :total-rows="pagination.totalRecords"
                :per-page="pagination.pageSize"
                first-number
                last-number
                align="right"
                prev-class="prev-item"
                next-class="next-item"
                class="mb-0"
                @input="onPageChange"
            >
                <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
                <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                </template>
            </b-pagination>
        </div>
    </div>
</template>

<script>
import {
    BTable, BBadge, BPagination, BButton, BOverlay, BSpinner, VBTooltip,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
    components: {
        BTable, BBadge, BPagination, BButton, BOverlay, BSpinner,
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    emits: ['update:total'],
    watch: {
        'pagination.totalRecords': {
            handler(val) {
                this.$emit('update:total', val)
            },
            immediate: true,
        },
    },
    computed: {
        tableData() {
            return this.updatedProperty
        },
        fromRecord() {
            if (!this.pagination.totalRecords) return 0
            return (this.pagination.currentPage - 1) * this.pagination.pageSize + 1
        },
        toRecord() {
            const end = this.pagination.currentPage * this.pagination.pageSize
            return end > this.pagination.totalRecords ? this.pagination.totalRecords : end
        },
    },
    data() {
        return {
            fields: [
                { key: 'property', label: 'Property', sortable: true },
                { key: 'user_name', label: 'Owner' },
                { key: 'sale_price', label: 'Sale', class: 'text-right' },
                { key: 'rent_price', label: 'Rent', class: 'text-right' },
                { key: 'updated_at', label: 'Submitted', sortable: true },
                { key: 'actions', label: '', class: 'text-right', thStyle: { width: '150px' } },
            ],
            updatedProperty: [],
            loading: false,
            actingId: null,
            pagination: {
                currentPage: 1,
                pageSize: 10,
                totalPages: 0,
                totalRecords: 0,
            },
        }
    },
    created() {
        this.fetchUpdatedPropertyList()
    },
    methods: {
        async confirmAction({
            title = 'Are you sure?',
            message = 'Do you want to continue?',
            okTitle = 'Yes',
            cancelTitle = 'Cancel',
            okVariant = 'primary',
        }) {
            return this.$bvModal.msgBoxConfirm(message, {
                title,
                size: 'sm',
                buttonSize: 'sm',
                okVariant,
                okTitle,
                cancelTitle,
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true,
            })
        },
        makeToast(variant, title, message) {
            this.$bvToast.toast(message, { title, variant, solid: true })
        },
        detailItems(item) {
            return [
                { label: 'Area (sqm.)', value: item.sqm || '-' },
                { label: 'Bathroom', value: item.bathroom ?? '-' },
                { label: 'Furniture', value: item.furniture_name || '-' },
                { label: 'Direction', value: item.direction_name || '-' },
                { label: 'View', value: item.view_remark || '-' },
                {
                    label: 'Available Date',
                    value: item.availble_date ? this.formatDate(item.availble_date) : '-',
                },
                { label: 'Sale Price', value: item.sale_price ? `฿${this.formatPrice(item.sale_price)}` : '-' },
                { label: 'Rent Price', value: item.rent_price ? `฿${this.formatPrice(item.rent_price)}` : '-' },
                { label: 'Status', value: item.status || '-' },
            ]
        },
        async approveTemp(item) {
            const confirmed = await this.confirmAction({
                title: 'Approve update',
                message: `Apply the submitted changes to ${item.property_code}?`,
                okTitle: 'Approve',
                okVariant: 'success',
            })
            if (!confirmed) return

            const userStorage = JSON.parse(localStorage.getItem('userData') || '{}')
            const payload = { ...item, updated_by: userStorage.id }

            this.actingId = item.id
            try {
                const { data } = await axios.post('v1/property/updated/approve', payload)
                if (data.success) {
                    this.makeToast('success', 'Approved', `${item.property_code} has been updated.`)
                }
                await this.fetchUpdatedPropertyList()
            } catch (e) {
                this.makeToast('danger', 'Error', 'Could not approve this update.')
            } finally {
                this.actingId = null
            }
        },
        async rejectTemp(item) {
            const confirmed = await this.confirmAction({
                title: 'Reject update',
                message: `Discard the submitted changes for ${item.property_code}? This can't be undone.`,
                okTitle: 'Reject',
                okVariant: 'danger',
            })
            if (!confirmed) return

            this.actingId = item.id
            try {
                await axios.delete(`v1/property/updated/delete/${item.id}`)
                this.makeToast('success', 'Update rejected', `Submitted changes for ${item.property_code} were discarded.`)
                await this.fetchUpdatedPropertyList()
            } catch (e) {
                this.makeToast('danger', 'Error', 'Could not reject this update.')
            } finally {
                this.actingId = null
            }
        },
        formatPrice(value) {
            return Number(value).toLocaleString('en-US', { minimumFractionDigits: 0 })
        },
        formatDate(value) {
            return new Date(value).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            })
        },
        timeAgo(value) {
            const diff = Date.now() - new Date(value).getTime()
            const mins = Math.floor(diff / 60000)
            if (mins < 1) return 'just now'
            if (mins < 60) return `${mins} min ago`
            const hrs = Math.floor(mins / 60)
            if (hrs < 24) return `${hrs} hr ago`
            const days = Math.floor(hrs / 24)
            if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`
            return this.formatDate(value)
        },
        refresh() {
            this.fetchUpdatedPropertyList()
        },
        async onPageChange(page) {
            this.pagination.currentPage = page
            await this.fetchUpdatedPropertyList()
        },
        async fetchUpdatedPropertyList() {
            this.loading = true
            try {
                const { data } = await axios.post('v1/property/updated/list', {
                    perPage: this.pagination.pageSize,
                    currentPage: this.pagination.currentPage,
                    order_by: 'created_at desc',
                    is_approve: 0,
                })
                if (data.success) {
                    this.updatedProperty = data.data
                    this.pagination = data.pagination
                }
            } catch (error) {
                const errMsg = error?.response?.data?.message || 'Something went wrong!'
                this.makeToast('danger', 'Error', errMsg)
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style scoped>
.pending-queue {
    font-size: 1.05rem;
}

/* Toolbar */
.queue-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.5rem;
}
.queue-count {
    display: flex;
    align-items: center;
    gap: 0.4rem;
}
.spin {
    animation: queue-spin 0.9s linear infinite;
}
@keyframes queue-spin {
    to { transform: rotate(360deg); }
}

/* Table */
.queue-table ::v-deep .queue-thead th {
    text-transform: uppercase;
    font-size: 0.85rem;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    border-top: 0;
}
.queue-table ::v-deep td {
    vertical-align: middle;
}

.cell-property {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.property-code {
    font-weight: 600;
    color: #80AC03;
}
a.property-code {
    text-decoration: none;
}
a.property-code:hover {
    text-decoration: underline;
}
.text-subtle {
    color: #6e6b7b;
    font-size: 0.9rem;
}
.cell-owner {
    display: flex;
    align-items: center;
}
.owner-link {
    color: inherit;
    text-decoration: none;
}
.owner-link:hover {
    color: #80AC03;
    text-decoration: underline;
}
.price {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.cell-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.15rem;
}

/* Expanded detail */
.detail-panel {
    background: #f8f8f8;
    border-left: 3px solid #80AC03;
    border-radius: 0.35rem;
    padding: 0.9rem 1rem;
    margin: 0.25rem 0;
}
.detail-panel__title {
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6e6b7b;
    margin-bottom: 0.6rem;
}
.detail-grid {
    display: flex;
    flex-wrap: nowrap;
    gap: 1.25rem;
    margin: 0;
    overflow-x: auto;
}
.detail-grid__item {
    flex: 1 0 auto;
    min-width: 110px;
    white-space: nowrap;
}
.detail-grid__item dt {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #a0a0a8;
    font-weight: 500;
    margin-bottom: 0.1rem;
}
.detail-grid__item dd {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 500;
    color: #5e5873;
}

/* Empty */
.queue-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem 1rem;
}

/* Footer */
.queue-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.85rem;
}

/* Dark theme */
.dark-layout .property-code { color: #a3d13a; }
.dark-layout .detail-panel { background: #283046; }
.dark-layout .detail-grid__item dd { color: #d0d2d6; }
.dark-layout .queue-table ::v-deep .queue-thead th { color: #b4b7bd; }
</style>
